import { sci, toHex, toHex0x, toPercent, toPercentUnit } from "./arithmetic-formatters";
import {
	DateTimeAsLocalFormatNoDateIfToday,
	dateTimeAsIso,
	dateTimeAsIsoNoDateIfToday,
	dateTimeAsLocal,
	dateTimeAsUS,
	dateTimeAsUSNoDateIfToday,
	dateTimeFromNow,
	dateTimeSystemFormatter,
	fromDays,
	fromHours,
	fromMicroSeconds,
	fromMilliSeconds,
	fromMinutes,
	fromNanoSeconds,
	fromSeconds,
	toClockMilliseconds,
	toClockSeconds,
	toDurationInDaysHoursMinutesSeconds,
	toDurationInHoursMinutesSeconds,
	toDurationInMilliseconds,
	toDurationInSeconds,
	toTimeTicks,
} from "./date-time-formatters";
import {
	binaryPrefix,
	currency,
	pressureUnitsScaler,
	shortUnitsScaler,
	siPrefix,
	simpleCountUnit,
} from "./symbol-formatters";
import type { UnitFormatter, UnitFormatterWithOptions } from "./types";
import { locale, stringFormatter, toFixedUnit } from "./value-formats";

const formatter = <
	TGroupName extends string,
	TFormatterName extends string,
	TFormatterId extends string,
	TFormatter extends UnitFormatter | UnitFormatterWithOptions,
>(
	group: TGroupName,
	name: TFormatterName,
	id: TFormatterId,
	transform: TFormatter,
) => ({ group, name, id, transform }) as const;

export const ALL_FORMATTERS = [
	formatter("Misc", "None", "none", toFixedUnit("")),
	formatter("Misc", "String", "string", stringFormatter),
	formatter("Misc", "Short", "short", shortUnitsScaler),
	formatter("Misc", "Percent (0-100)", "percent", toPercent),
	formatter("Misc", "Percent (0.0-1.0)", "percentunit", toPercentUnit),
	formatter("Misc", "Humidity (%H)", "humidity", toFixedUnit("%H")),
	formatter("Misc", "Decibel", "dB", toFixedUnit("dB")),
	formatter("Misc", "Hexadecimal (0x)", "hex0x", toHex0x),
	formatter("Misc", "Hexadecimal", "hex", toHex),
	formatter("Misc", "Scientific notation", "sci", sci),
	formatter("Misc", "Locale format", "locale", locale),
	formatter("Misc", "Pixels", "pixel", toFixedUnit("px")),

	formatter("Acceleration", "Meters/sec²", "accMS2", toFixedUnit("m/sec²")),
	formatter("Acceleration", "Feet/sec²", "accFS2", toFixedUnit("f/sec²")),
	formatter("Acceleration", "G unit", "accG", toFixedUnit("g")),

	formatter("Angle", "Degrees (°)", "degree", toFixedUnit("°")),
	formatter("Angle", "Radians", "radian", toFixedUnit("rad")),
	formatter("Angle", "Gradian", "grad", toFixedUnit("grad")),
	formatter("Angle", "Arc Minutes", "arcmin", toFixedUnit("arcmin")),
	formatter("Angle", "Arc Seconds", "arcsec", toFixedUnit("arcsec")),

	formatter("Area", "Square Meters (m²)", "areaM2", toFixedUnit("m²")),
	formatter("Area", "Square Feet (ft²)", "areaF2", toFixedUnit("ft²")),
	formatter("Area", "Square Miles (mi²)", "areaMI2", toFixedUnit("mi²")),

	formatter("Computation", "FLOP/s", "flops", siPrefix("FLOPS")),
	formatter("Computation", "MFLOP/s", "mflops", siPrefix("FLOPS", 2)),
	formatter("Computation", "GFLOP/s", "gflops", siPrefix("FLOPS", 3)),
	formatter("Computation", "TFLOP/s", "tflops", siPrefix("FLOPS", 4)),
	formatter("Computation", "PFLOP/s", "pflops", siPrefix("FLOPS", 5)),
	formatter("Computation", "EFLOP/s", "eflops", siPrefix("FLOPS", 6)),
	formatter("Computation", "ZFLOP/s", "zflops", siPrefix("FLOPS", 7)),
	formatter("Computation", "YFLOP/s", "yflops", siPrefix("FLOPS", 8)),

	formatter("Concentration", "parts-per-million (ppm)", "ppm", toFixedUnit("ppm")),
	formatter("Concentration", "parts-per-billion (ppb)", "conppb", toFixedUnit("ppb")),
	formatter("Concentration", "nanogram per cubic meter (ng/m³)", "conngm3", toFixedUnit("ng/m³")),
	formatter("Concentration", "nanogram per normal cubic meter (ng/Nm³)", "conngNm3", toFixedUnit("ng/Nm³")),
	formatter("Concentration", "microgram per cubic meter (μg/m³)", "conμgm3", toFixedUnit("μg/m³")),
	formatter("Concentration", "microgram per normal cubic meter (μg/Nm³)", "conμgNm3", toFixedUnit("μg/Nm³")),
	formatter("Concentration", "milligram per cubic meter (mg/m³)", "conmgm3", toFixedUnit("mg/m³")),
	formatter("Concentration", "milligram per normal cubic meter (mg/Nm³)", "conmgNm3", toFixedUnit("mg/Nm³")),
	formatter("Concentration", "gram per cubic meter (g/m³)", "congm3", toFixedUnit("g/m³")),
	formatter("Concentration", "gram per normal cubic meter (g/Nm³)", "congNm3", toFixedUnit("g/Nm³")),
	formatter("Concentration", "milligrams per decilitre (mg/dL)", "conmgdL", toFixedUnit("mg/dL")),
	formatter("Concentration", "millimoles per litre (mmol/L)", "conmmolL", toFixedUnit("mmol/L")),

	formatter("Currency", "Dollars ($)", "currencyUSD", currency("$")),
	formatter("Currency", "Pounds (£)", "currencyGBP", currency("£")),
	formatter("Currency", "Euro (€)", "currencyEUR", currency("€")),
	formatter("Currency", "Yen (¥)", "currencyJPY", currency("¥")),
	formatter("Currency", "Rubles (₽)", "currencyRUB", currency("₽")),
	formatter("Currency", "Hryvnias (₴)", "currencyUAH", currency("₴")),
	formatter("Currency", "Real (R$)", "currencyBRL", currency("R$")),
	formatter("Currency", "Danish Krone (kr)", "currencyDKK", currency("kr", true)),
	formatter("Currency", "Icelandic Króna (kr)", "currencyISK", currency("kr", true)),
	formatter("Currency", "Norwegian Krone (kr)", "currencyNOK", currency("kr", true)),
	formatter("Currency", "Swedish Krona (kr)", "currencySEK", currency("kr", true)),
	formatter("Currency", "Czech koruna (czk)", "currencyCZK", currency("czk")),
	formatter("Currency", "Swiss franc (CHF)", "currencyCHF", currency("CHF")),
	formatter("Currency", "Polish Złoty (PLN)", "currencyPLN", currency("PLN")),
	formatter("Currency", "Bitcoin (฿)", "currencyBTC", currency("฿")),
	formatter("Currency", "Milli Bitcoin (฿)", "currencymBTC", currency("mBTC")),
	formatter("Currency", "Micro Bitcoin (฿)", "currencyμBTC", currency("μBTC")),
	formatter("Currency", "South African Rand (R)", "currencyZAR", currency("R")),
	formatter("Currency", "Indian Rupee (₹)", "currencyINR", currency("₹")),
	formatter("Currency", "South Korean Won (₩)", "currencyKRW", currency("₩")),
	formatter("Currency", "Indonesian Rupiah (Rp)", "currencyIDR", currency("Rp")),
	formatter("Currency", "Philippine Peso (PHP)", "currencyPHP", currency("PHP")),
	formatter("Currency", "Vietnamese Dong (VND)", "currencyVND", currency("đ", true)),

	formatter("Data", "bytes(IEC)", "bytes", binaryPrefix("B")),
	formatter("Data", "bytes(SI)", "decbytes", siPrefix("B")),
	formatter("Data", "bits(IEC)", "bits", binaryPrefix("b")),
	formatter("Data", "bits(SI)", "decbits", siPrefix("b")),
	formatter("Data", "kibibytes", "kbytes", binaryPrefix("B", 1)),
	formatter("Data", "kilobytes", "deckbytes", siPrefix("B", 1)),
	formatter("Data", "mebibytes", "mbytes", binaryPrefix("B", 2)),
	formatter("Data", "megabytes", "decmbytes", siPrefix("B", 2)),
	formatter("Data", "gibibytes", "gbytes", binaryPrefix("B", 3)),
	formatter("Data", "gigabytes", "decgbytes", siPrefix("B", 3)),
	formatter("Data", "tebibytes", "tbytes", binaryPrefix("B", 4)),
	formatter("Data", "terabytes", "dectbytes", siPrefix("B", 4)),
	formatter("Data", "pebibytes", "pbytes", binaryPrefix("B", 5)),
	formatter("Data", "petabytes", "decpbytes", siPrefix("B", 5)),

	formatter("Data rate", "packets/sec", "pps", siPrefix("p/s")),
	formatter("Data rate", "bytes/sec(IEC)", "binBps", binaryPrefix("B/s")),
	formatter("Data rate", "bytes/sec(SI)", "Bps", siPrefix("B/s")),
	formatter("Data rate", "bits/sec(IEC)", "binbps", binaryPrefix("b/s")),
	formatter("Data rate", "bits/sec(SI)", "bps", siPrefix("b/s")),
	formatter("Data rate", "kibibytes/sec", "KiBs", binaryPrefix("B/s", 1)),
	formatter("Data rate", "kibibits/sec", "Kibits", binaryPrefix("b/s", 1)),
	formatter("Data rate", "kilobytes/sec", "KBs", siPrefix("B/s", 1)),
	formatter("Data rate", "kilobits/sec", "Kbits", siPrefix("b/s", 1)),
	formatter("Data rate", "mibibytes/sec", "MiBs", binaryPrefix("B/s", 2)),
	formatter("Data rate", "mibibits/sec", "Mibits", binaryPrefix("b/s", 2)),
	formatter("Data rate", "megabytes/sec", "MBs", siPrefix("B/s", 2)),
	formatter("Data rate", "megabits/sec", "Mbits", siPrefix("b/s", 2)),
	formatter("Data rate", "gibibytes/sec", "GiBs", binaryPrefix("B/s", 3)),
	formatter("Data rate", "gibibits/sec", "Gibits", binaryPrefix("b/s", 3)),
	formatter("Data rate", "gigabytes/sec", "GBs", siPrefix("B/s", 3)),
	formatter("Data rate", "gigabits/sec", "Gbits", siPrefix("b/s", 3)),
	formatter("Data rate", "tebibytes/sec", "TiBs", binaryPrefix("B/s", 4)),
	formatter("Data rate", "tebibits/sec", "Tibits", binaryPrefix("b/s", 4)),
	formatter("Data rate", "terabytes/sec", "TBs", siPrefix("B/s", 4)),
	formatter("Data rate", "terabits/sec", "Tbits", siPrefix("b/s", 4)),
	formatter("Data rate", "pibibytes/sec", "PiBs", binaryPrefix("B/s", 5)),
	formatter("Data rate", "pibibits/sec", "Pibits", binaryPrefix("b/s", 5)),
	formatter("Data rate", "petabytes/sec", "PBs", siPrefix("B/s", 5)),
	formatter("Data rate", "petabits/sec", "Pbits", siPrefix("b/s", 5)),

	formatter("Date & time", "Datetime ISO", "dateTimeAsIso", dateTimeAsIso),
	formatter("Date & time", "Datetime ISO (No date if today)", "dateTimeAsIsoNoDateIfToday", dateTimeAsIsoNoDateIfToday),
	formatter("Date & time", "Datetime US", "dateTimeAsUS", dateTimeAsUS),
	formatter("Date & time", "Datetime US (No date if today)", "dateTimeAsUSNoDateIfToday", dateTimeAsUSNoDateIfToday),
	formatter("Date & time", "Datetime local", "dateTimeAsLocal", dateTimeAsLocal),
	formatter(
		"Date & time",
		"Datetime local (No date if today)",
		"dateTimeAsLocalNoDateIfToday",
		DateTimeAsLocalFormatNoDateIfToday,
	),
	formatter("Date & time", "Datetime default", "dateTimeAsSystem", dateTimeSystemFormatter),
	formatter("Date & time", "From Now", "dateTimeFromNow", dateTimeFromNow),

	formatter("Energy", "Watt (W)", "watt", siPrefix("W")),
	formatter("Energy", "Kilowatt (kW)", "kwatt", siPrefix("W", 1)),
	formatter("Energy", "Megawatt (MW)", "megwatt", siPrefix("W", 2)),
	formatter("Energy", "Gigawatt (GW)", "gwatt", siPrefix("W", 3)),
	formatter("Energy", "Milliwatt (mW)", "mwatt", siPrefix("W", -1)),
	formatter("Energy", "Watt per square meter (W/m²)", "Wm2", toFixedUnit("W/m²")),
	formatter("Energy", "Volt-ampere (VA)", "voltamp", siPrefix("VA")),
	formatter("Energy", "Kilovolt-ampere (kVA)", "kvoltamp", siPrefix("VA", 1)),
	formatter("Energy", "Volt-ampere reactive (var)", "voltampreact", siPrefix("var")),
	formatter("Energy", "Kilovolt-ampere reactive (kVAr)", "kvoltampreact", siPrefix("VAr", 1)),
	formatter("Energy", "Watt-hour (Wh)", "watth", siPrefix("Wh")),
	formatter("Energy", "Watt-hour per Kilogram (Wh/kg)", "watthperkg", siPrefix("Wh/kg")),
	formatter("Energy", "Kilowatt-hour (kWh)", "kwatth", siPrefix("Wh", 1)),
	formatter("Energy", "Kilowatt-min (kWm)", "kwattm", siPrefix("W-Min", 1)),
	formatter("Energy", "Ampere-hour (Ah)", "amph", siPrefix("Ah")),
	formatter("Energy", "Kiloampere-hour (kAh)", "kamph", siPrefix("Ah", 1)),
	formatter("Energy", "Milliampere-hour (mAh)", "mamph", siPrefix("Ah", -1)),
	formatter("Energy", "Joule (J)", "joule", siPrefix("J")),
	formatter("Energy", "Electron volt (eV)", "ev", siPrefix("eV")),
	formatter("Energy", "Ampere (A)", "amp", siPrefix("A")),
	formatter("Energy", "Kiloampere (kA)", "kamp", siPrefix("A", 1)),
	formatter("Energy", "Milliampere (mA)", "mamp", siPrefix("A", -1)),
	formatter("Energy", "Volt (V)", "volt", siPrefix("V")),
	formatter("Energy", "Kilovolt (kV)", "kvolt", siPrefix("V", 1)),
	formatter("Energy", "Millivolt (mV)", "mvolt", siPrefix("V", -1)),
	formatter("Energy", "Decibel-milliwatt (dBm)", "dBm", siPrefix("dBm")),
	formatter("Energy", "Ohm (Ω)", "ohm", siPrefix("Ω")),
	formatter("Energy", "Kiloohm (kΩ)", "kohm", siPrefix("Ω", 1)),
	formatter("Energy", "Megaohm (MΩ)", "Mohm", siPrefix("Ω", 2)),
	formatter("Energy", "Farad (F)", "farad", siPrefix("F")),
	formatter("Energy", "Microfarad (µF)", "µfarad", siPrefix("F", -2)),
	formatter("Energy", "Nanofarad (nF)", "nfarad", siPrefix("F", -3)),
	formatter("Energy", "Picofarad (pF)", "pfarad", siPrefix("F", -4)),
	formatter("Energy", "Femtofarad (fF)", "ffarad", siPrefix("F", -5)),
	formatter("Energy", "Henry (H)", "henry", siPrefix("H")),
	formatter("Energy", "Millihenry (mH)", "mhenry", siPrefix("H", -1)),
	formatter("Energy", "Microhenry (µH)", "µhenry", siPrefix("H", -2)),
	formatter("Energy", "Lumens (Lm)", "lumens", siPrefix("Lm")),

	formatter("Flow", "Gallons/min (gpm)", "flowgpm", toFixedUnit("gpm")),
	formatter("Flow", "Cubic meters/sec (cms)", "flowcms", toFixedUnit("cms")),
	formatter("Flow", "Cubic feet/sec (cfs)", "flowcfs", toFixedUnit("cfs")),
	formatter("Flow", "Cubic feet/min (cfm)", "flowcfm", toFixedUnit("cfm")),
	formatter("Flow", "Litre/hour", "litreh", toFixedUnit("L/h")),
	formatter("Flow", "Litre/min (L/min)", "flowlpm", toFixedUnit("L/min")),
	formatter("Flow", "milliLitre/min (mL/min)", "flowmlpm", toFixedUnit("mL/min")),
	formatter("Flow", "Lux (lx)", "lux", toFixedUnit("lux")),

	formatter("Force", "Newton-meters (Nm)", "forceNm", siPrefix("Nm")),
	formatter("Force", "Kilonewton-meters (kNm)", "forcekNm", siPrefix("Nm", 1)),
	formatter("Force", "Newtons (N)", "forceN", siPrefix("N")),
	formatter("Force", "Kilonewtons (kN)", "forcekN", siPrefix("N", 1)),

	formatter("Hash rate", "hashes/sec", "Hs", siPrefix("H/s")),
	formatter("Hash rate", "kilohashes/sec", "KHs", siPrefix("H/s", 1)),
	formatter("Hash rate", "megahashes/sec", "MHs", siPrefix("H/s", 2)),
	formatter("Hash rate", "gigahashes/sec", "GHs", siPrefix("H/s", 3)),
	formatter("Hash rate", "terahashes/sec", "THs", siPrefix("H/s", 4)),
	formatter("Hash rate", "petahashes/sec", "PHs", siPrefix("H/s", 5)),
	formatter("Hash rate", "exahashes/sec", "EHs", siPrefix("H/s", 6)),

	formatter("Mass", "milligram (mg)", "massmg", siPrefix("g", -1)),
	formatter("Mass", "gram (g)", "massg", siPrefix("g")),
	formatter("Mass", "pound (lb)", "masslb", toFixedUnit("lb")),
	formatter("Mass", "kilogram (kg)", "masskg", siPrefix("g", 1)),
	formatter("Mass", "metric ton (t)", "masst", toFixedUnit("t")),

	formatter("Length", "millimeter (mm)", "lengthmm", siPrefix("m", -1)),
	formatter("Length", "inch (in)", "lengthin", toFixedUnit("in")),
	formatter("Length", "feet (ft)", "lengthft", toFixedUnit("ft")),
	formatter("Length", "meter (m)", "lengthm", siPrefix("m")),
	formatter("Length", "kilometer (km)", "lengthkm", siPrefix("m", 1)),
	formatter("Length", "mile (mi)", "lengthmi", toFixedUnit("mi")),

	formatter("Pressure", "Millibars", "pressurembar", siPrefix("bar", -1)),
	formatter("Pressure", "Bars", "pressurebar", siPrefix("bar")),
	formatter("Pressure", "Kilobars", "pressurekbar", siPrefix("bar", 1)),
	formatter("Pressure", "Pascals", "pressurepa", siPrefix("Pa")),
	formatter("Pressure", "Hectopascals", "pressurehpa", toFixedUnit("hPa")),
	formatter("Pressure", "Kilopascals", "pressurekpa", toFixedUnit("kPa")),
	formatter("Pressure", "Inches of mercury", "pressurehg", toFixedUnit('"Hg')),
	formatter("Pressure", "PSI", "pressurepsi", pressureUnitsScaler),

	formatter("Radiation", "Becquerel (Bq)", "radbq", siPrefix("Bq")),
	formatter("Radiation", "curie (Ci)", "radci", siPrefix("Ci")),
	formatter("Radiation", "Gray (Gy)", "radgy", siPrefix("Gy")),
	formatter("Radiation", "rad", "radrad", siPrefix("rad")),
	formatter("Radiation", "Sievert (Sv)", "radsv", siPrefix("Sv")),
	formatter("Radiation", "milliSievert (mSv)", "radmsv", siPrefix("Sv", -1)),
	formatter("Radiation", "microSievert (µSv)", "radusv", siPrefix("Sv", -2)),
	formatter("Radiation", "rem", "radrem", siPrefix("rem")),
	formatter("Radiation", "Exposure (C/kg)", "radexpckg", siPrefix("C/kg")),
	formatter("Radiation", "roentgen (R)", "radr", siPrefix("R")),
	formatter("Radiation", "Sievert/hour (Sv/h)", "radsvh", siPrefix("Sv/h")),
	formatter("Radiation", "milliSievert/hour (mSv/h)", "radmsvh", siPrefix("Sv/h", -1)),
	formatter("Radiation", "microSievert/hour (µSv/h)", "radusvh", siPrefix("Sv/h", -2)),

	formatter("Rotational Speed", "Revolutions per minute (rpm)", "rotrpm", toFixedUnit("rpm")),
	formatter("Rotational Speed", "Hertz (Hz)", "rothz", siPrefix("Hz")),
	formatter("Rotational Speed", "Radians per second (rad/s)", "rotrads", toFixedUnit("rad/s")),
	formatter("Rotational Speed", "Degrees per second (°/s)", "rotdegs", toFixedUnit("°/s")),

	formatter("Temperature", "Celsius (°C)", "celsius", toFixedUnit("°C")),
	formatter("Temperature", "Fahrenheit (°F)", "fahrenheit", toFixedUnit("°F")),
	formatter("Temperature", "Kelvin (K)", "kelvin", toFixedUnit("K")),

	formatter("Time", "Hertz (1/s)", "hertz", siPrefix("Hz")),
	formatter("Time", "nanoseconds (ns)", "ns", fromNanoSeconds),
	formatter("Time", "microseconds (µs)", "µs", fromMicroSeconds),
	formatter("Time", "milliseconds (ms)", "ms", fromMilliSeconds),
	formatter("Time", "seconds (s)", "s", fromSeconds),
	formatter("Time", "minutes (m)", "m", fromMinutes),
	formatter("Time", "hours (h)", "h", fromHours),
	formatter("Time", "days (d)", "d", fromDays),
	formatter("Time", "duration (ms)", "dtdurationms", toDurationInMilliseconds),
	formatter("Time", "duration (s)", "dtdurations", toDurationInSeconds),
	formatter("Time", "duration (hh:mm:ss)", "dthms", toDurationInHoursMinutesSeconds),
	formatter("Time", "duration (d hh:mm:ss)", "dtdhms", toDurationInDaysHoursMinutesSeconds),
	formatter("Time", "Timeticks (s/100)", "timeticks", toTimeTicks),
	formatter("Time", "clock (ms)", "clockms", toClockMilliseconds),
	formatter("Time", "clock (s)", "clocks", toClockSeconds),

	formatter("Throughput", "counts/sec (cps)", "cps", simpleCountUnit("c/s")),
	formatter("Throughput", "ops/sec (ops)", "ops", simpleCountUnit("ops/s")),
	formatter("Throughput", "requests/sec (rps)", "reqps", simpleCountUnit("req/s")),
	formatter("Throughput", "reads/sec (rps)", "rps", simpleCountUnit("rd/s")),
	formatter("Throughput", "writes/sec (wps)", "wps", simpleCountUnit("wr/s")),
	formatter("Throughput", "I/O ops/sec (iops)", "iops", simpleCountUnit("io/s")),
	formatter("Throughput", "counts/min (cpm)", "cpm", simpleCountUnit("c/m")),
	formatter("Throughput", "ops/min (opm)", "opm", simpleCountUnit("ops/m")),
	formatter("Throughput", "reads/min (rpm)", "rpm", simpleCountUnit("rd/m")),
	formatter("Throughput", "writes/min (wpm)", "wpm", simpleCountUnit("wr/m")),

	formatter("Velocity", "meters/second (m/s)", "velocityms", toFixedUnit("m/s")),
	formatter("Velocity", "kilometers/hour (km/h)", "velocitykmh", toFixedUnit("km/h")),
	formatter("Velocity", "miles/hour (mph)", "velocitymph", toFixedUnit("mph")),
	formatter("Velocity", "knot (kn)", "velocityknot", toFixedUnit("kn")),

	formatter("Volume", "millilitre (mL)", "mlitre", siPrefix("L", -1)),
	formatter("Volume", "litre (L)", "litre", siPrefix("L")),
	formatter("Volume", "cubic meter", "m3", toFixedUnit("m³")),
	formatter("Volume", "Normal cubic meter", "Nm3", toFixedUnit("Nm³")),
	formatter("Volume", "cubic decimeter", "dm3", toFixedUnit("dm³")),
	formatter("Volume", "gallons", "gallons", toFixedUnit("gal")),
];

export type Formatter = (typeof ALL_FORMATTERS)[number];
